import React, { useEffect, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Clock, Play, Coffee, ArrowClockwise, Stop } from "@phosphor-icons/react";

function fmtMs(ms) {
  if (!ms || ms < 0) return "0h 0m";
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  return `${h}h ${m}m`;
}

export default function MyDuty() {
  const [cur, setCur] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [a, b] = await Promise.all([
        api.get("/my-duty/current"),
        api.get("/my-duty/history?limit=30"),
      ]);
      setCur(a.data); setHistory(b.data);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);

  const act = async (path, msg) => {
    setBusy(true);
    try { await api.post(path); toast.success(msg); load(); }
    catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const active = cur?.status === "ON_DUTY_ACTIVE";
  const onBreak = cur?.status === "ON_BREAK";

  return (
    <AppShell title="My Duty" subtitle="Personal time tracking · immutable">
      <div className={`rounded-lg p-6 mb-6 ${active ? "bg-[#2D5A27] text-white" : onBreak ? "bg-[#C89E3E] text-white" : "bg-white border border-[#E5E2D9]"}`} data-testid="my-duty-hero">
        <div className="text-[10px] uppercase tracking-[0.2em] opacity-70">
          {cur ? cur.status : "Off duty"}
        </div>
        <div className="font-heading text-3xl mt-1">
          {cur ? (onBreak ? "On break" : "On duty") : "Ready to start"}
        </div>
        {cur && (
          <div className="grid grid-cols-4 gap-3 mt-4">
            <Kpi inverse={active || onBreak} label="Active" v={fmtMs(cur.active_ms)}/>
            <Kpi inverse={active || onBreak} label="Break" v={fmtMs(cur.break_ms)}/>
            <Kpi inverse={active || onBreak} label="Idle" v={fmtMs(cur.inactive_ms)}/>
            <Kpi inverse={active || onBreak} label="Total" v={fmtMs(cur.total_ms)}/>
          </div>
        )}
        <div className="flex gap-2 mt-5">
          {!cur && (
            <button onClick={() => act("/my-duty/start", "Duty started")} disabled={busy} data-testid="my-start"
                    className="h-11 px-5 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-sm font-medium inline-flex items-center gap-2">
              <Play size={14} weight="fill"/>Start duty
            </button>
          )}
          {active && (
            <>
              <button onClick={() => act("/my-duty/break", "On break")} disabled={busy} data-testid="my-break"
                      className="h-11 px-5 bg-white/15 hover:bg-white/25 text-white border border-white/20 rounded-md text-sm inline-flex items-center gap-2">
                <Coffee size={14}/>Break
              </button>
              <button onClick={() => act("/my-duty/end", "Duty ended")} disabled={busy} data-testid="my-end"
                      className="h-11 px-5 bg-white/15 hover:bg-white/25 text-white border border-white/20 rounded-md text-sm inline-flex items-center gap-2">
                <Stop size={14} weight="fill"/>End duty
              </button>
            </>
          )}
          {onBreak && (
            <button onClick={() => act("/my-duty/resume", "Resumed")} disabled={busy} data-testid="my-resume"
                    className="h-11 px-5 bg-white/25 text-white border border-white/30 rounded-md text-sm inline-flex items-center gap-2">
              <ArrowClockwise size={14}/>Resume
            </button>
          )}
        </div>
      </div>

      <div className="bg-white border border-[#E5E2D9] rounded-md">
        <div className="px-5 py-3 border-b border-[#E5E2D9] text-[10px] uppercase tracking-[0.2em] text-[#5C5A55] flex items-center gap-1">
          <Clock size={11}/>My session history
        </div>
        <table className="w-full text-sm" data-testid="my-history">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-[#8A8780] bg-[#FAFAF7]">
              <th className="px-5 py-2">Start</th>
              <th className="px-5 py-2">End</th>
              <th className="px-5 py-2 text-right">Active</th>
              <th className="px-5 py-2 text-right">Break</th>
              <th className="px-5 py-2 text-right">Idle</th>
              <th className="px-5 py-2 text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {history.length === 0 && <tr><td colSpan={6} className="px-5 py-8 text-center text-[#8A8780]">No sessions yet.</td></tr>}
            {history.map(s => (
              <tr key={s.id} className="border-t border-[#E5E2D9]">
                <td className="px-5 py-2 font-mono text-xs">{new Date(s.start_time).toLocaleString()}</td>
                <td className="px-5 py-2 font-mono text-xs">{s.end_time ? new Date(s.end_time).toLocaleString() : <span className="text-[#2D5A27]">● live</span>}</td>
                <td className="px-5 py-2 text-right font-mono">{fmtMs(s.active_ms)}</td>
                <td className="px-5 py-2 text-right font-mono">{fmtMs(s.break_ms)}</td>
                <td className="px-5 py-2 text-right font-mono">{fmtMs(s.inactive_ms)}</td>
                <td className="px-5 py-2 text-right font-mono">{fmtMs(s.total_ms)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AppShell>
  );
}

const Kpi = ({ label, v, inverse }) => (
  <div className={`${inverse ? "bg-white/15 border-white/20 text-white" : "bg-[#F7F6F2] border-[#E5E2D9]"} border rounded px-3 py-2`}>
    <div className="text-[10px] uppercase tracking-[0.2em] opacity-70">{label}</div>
    <div className="font-heading text-xl leading-tight">{v}</div>
  </div>
);
